// Shared pieces for the website lead magnets: the four public POST endpoints
// (backend/myapp lead magnets), the one analytics event, and the browser flag
// that keeps the slide-in away from anyone who already left an email.
//
// Every endpoint answers `{status}` on success and `{code, error}` on failure,
// where `error` is a plain sentence written for the visitor.
import { API_URL } from "@/components/config/config";
import fetchWithAuth from "@/hooks/fetchWithAuth";
import { getWebsiteTrackingContext, trackWebsiteAction } from "@/lib/analytics/website";

export type KitSlug =
  | "customer-service"
  | "sales"
  | "retail"
  | "receptionist"
  | "call-center"
  | "phone-screen";

// The six screening kits. Each slug is also the /interview-questions/<slug>
// page the kit is built from (lib/hubs/iq/<slug>.js).
export const KITS: { slug: KitSlug; title: string }[] = [
  { slug: "customer-service", title: "Customer service" },
  { slug: "sales", title: "Sales" },
  { slug: "retail", title: "Retail" },
  { slug: "receptionist", title: "Receptionist" },
  { slug: "call-center", title: "Call center" },
  { slug: "phone-screen", title: "Phone screen" },
];

export const DEMO_ROLES = [
  { value: "customer_service", label: "Customer service" },
  { value: "sales", label: "Sales" },
  { value: "retail", label: "Retail" },
] as const;
export type DemoRole = (typeof DEMO_ROLES)[number]["value"];

export const kitFromSlug = (slug: string | undefined): KitSlug | undefined =>
  KITS.find((kit) => kit.slug === slug)?.slug;

const kitTitle = (slug: KitSlug): string => KITS.find((kit) => kit.slug === slug)?.title ?? "";

// "customer service screening kit", and "phone screen kit" (a phone screen is
// already a screening call).
export const kitNoun = (slug: KitSlug): string =>
  slug === "phone-screen" ? "phone screen kit" : `${kitTitle(slug).toLowerCase()} screening kit`;

// "Customer service screening kit" for the kit page title.
export const kitPageTitle = (slug: KitSlug): string => {
  const noun = kitNoun(slug);
  return noun.charAt(0).toUpperCase() + noun.slice(1);
};

// One analytics value per way of leaving an email.
export type LeadMagnet = "try_interview" | "kit" | "tool_result" | "slide_in" | "newsletter";

// Where on the site the form sat. It rides on every request, so the admin
// Leads tab and the offer view event can tell the five surfaces apart. "page"
// is a form that is the whole page (/newsletter).
export type LeadPlacement = "panel" | "inline" | "slide_in" | "tool" | "page";

export const GENERIC_ERROR = "Something went wrong. Please try again.";
export const MARKETING_CONSENT_LABEL = "Also send me hiring tips and product news. Unsubscribe anytime.";

// `data` is the response on success; on failure it is null, `message` is the
// sentence to show and `code` is the server's error code (empty when none).
type PostResult = { data: Record<string, unknown> | null; message: string; code: string };

// `page` and the analytics visitor id (only present with analytics consent)
// ride along on every request. Failures return the server's sentence, or a
// fixed one when there is none, so a form can show them under its button.
export async function postLead(
  endpoint: "try-interview" | "kit" | "tool-result" | "newsletter",
  body: Record<string, unknown>,
): Promise<PostResult> {
  try {
    const visitorId = getWebsiteTrackingContext()?.visitorId;
    const response = await fetchWithAuth(`${API_URL}/lead-magnets/${endpoint}/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...body,
        page: window.location.pathname,
        ...(visitorId ? { visitorId } : {}),
      }),
      omitUid: true,
      retry: false,
      proxy: false,
      cache: "no-store",
      timeoutMs: 20_000,
    });
    const json = await response.json().catch(() => null);
    if (!response.ok) {
      const sentence = typeof json?.error === "string" ? json.error.trim() : "";
      const code = typeof json?.code === "string" ? json.code : "";
      return { data: null, message: sentence || GENERIC_ERROR, code };
    }
    return { data: json && typeof json === "object" ? json : {}, message: "", code: "" };
  } catch {
    return { data: null, message: GENERIC_ERROR, code: "" };
  }
}

const SUBMITTED_KEY = "aiia:lead-submitted";

export function hasSubmittedLead(): boolean {
  try {
    return window.localStorage.getItem(SUBMITTED_KEY) === "1";
  } catch {
    return false;
  }
}

// The slide-in listens for this, so it closes when any other form sends.
export const LEAD_SUBMITTED_EVENT = "aiia:lead-submitted";

// Called once for every successful submission, on every form.
export function recordLeadSubmitted(magnet: LeadMagnet): void {
  try {
    window.localStorage.setItem(SUBMITTED_KEY, "1");
  } catch {
    // Storage can be blocked; the slide-in may then show once more.
  }
  trackWebsiteAction("product_event", { target: "lead_magnet_submitted", metric: magnet });
  window.dispatchEvent(new CustomEvent<LeadMagnet>(LEAD_SUBMITTED_EVENT, { detail: magnet }));
}

// Called when an offer is first seen: the slide-in opening, or a panel or box
// on the page scrolling at least half into view.
export function trackOfferViewed(placement: LeadPlacement): void {
  trackWebsiteAction("product_event", { target: "lead_offer_viewed", metric: placement });
}

